import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
  FarklePlayerRecord,
  FarklePlayerControlAction,
  leaveFarkleQueue,
  renameFarklePlayer,
  subscribeToFarklePlayers,
  upsertFarklePlayer,
  updateFarklePlayerStatus,
  sendFarklePlayerAction,
} from '../games/farkle/lobby';
import { FarkleStateDocument } from '../games/farkle/logic';

const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `player_${Math.random().toString(36).slice(2, 10)}`;
};

const storageKey = (partyId: string) => `qroke:farkle:player:${partyId}`;

const FarkleJoinPage: React.FC = () => {
  const { partyId } = useParams<{ partyId: string }>();
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [playerDoc, setPlayerDoc] = useState<FarklePlayerRecord | null>(null);
  const [lobbyPlayers, setLobbyPlayers] = useState<FarklePlayerRecord[]>([]);
  const [phase, setPhase] = useState<FarkleStateDocument['phase'] | 'idle'>('idle');
  const [gameDoc, setGameDoc] = useState<FarkleStateDocument | null>(null);
  const [statusMessage, setStatusMessage] = useState('Enter your name to jump in line.');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedDiceIndices, setSelectedDiceIndices] = useState<number[]>([]);
  const [pendingActionNonce, setPendingActionNonce] = useState<number | null>(null);
  const [controllerStatus, setControllerStatus] = useState('Waiting to be seated.');
  const [controllerError, setControllerError] = useState<string | null>(null);
  const [showFarkleOverlay, setShowFarkleOverlay] = useState(false);
  const [farkleDice, setFarkleDice] = useState<number[]>([]);
  const farkleOverlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFarkleEventRef = useRef<number | null>(null);
  const turnSoundCtxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (!partyId) return;
    const stored = localStorage.getItem(storageKey(partyId));
    if (stored) {
      setPlayerId(stored);
    }
  }, [partyId]);

  useEffect(() => {
    return () => {
      if (farkleOverlayTimerRef.current) {
        clearTimeout(farkleOverlayTimerRef.current);
      }
      if (turnSoundCtxRef.current) {
        turnSoundCtxRef.current.close?.().catch(() => undefined);
      }
    };
  }, []);

  useEffect(() => {
    if (!partyId) return;
    const unsub = subscribeToFarklePlayers(partyId, (records) => {
      setLobbyPlayers(records);
    });
    return () => unsub();
  }, [partyId]);

  useEffect(() => {
    if (!partyId) return;
    const ref = doc(db, 'parties', partyId, 'farkle', 'state');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data() as FarkleStateDocument;
        setGameDoc(data);
        setPhase(data.phase);
      } else {
        setGameDoc(null);
        setPhase('idle');
      }
    });
    return () => unsub();
  }, [partyId]);

  useEffect(() => {
    if (!partyId || !playerId) return;
    const ref = doc(db, 'parties', partyId, 'farkle_players', playerId);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data() as any;
        const record: FarklePlayerRecord = {
          id: snap.id,
          name: data.name ?? 'Player',
          status: data.status ?? 'waiting',
          joinedAt: data.joinedAt?.toMillis?.() ?? Date.now(),
          currentGameId: data.currentGameId ?? null,
          seatIndex: typeof data.seatIndex === 'number' ? data.seatIndex : null,
          gamesPlayed: data.gamesPlayed ?? 0,
          lastUpdatedAt: data.lastUpdatedAt?.toMillis?.() ?? Date.now(),
          controlAction: data.controlAction ?? null,
          controlAckNonce: typeof data.controlAckNonce === 'number' ? data.controlAckNonce : null,
          controlAckAt: data.controlAckAt?.toMillis?.() ?? null,
        };
        setPlayerDoc(record);
        if (!name) {
          setName(record.name);
        }
      } else {
        setPlayerDoc(null);
      }
    });
    return () => unsub();
  }, [partyId, playerId, name]);

  const waitingPlayers = useMemo(() => lobbyPlayers.filter((p) => p.status === 'waiting'), [lobbyPlayers]);
  const queuePosition = useMemo(() => {
    if (!playerDoc || playerDoc.status !== 'waiting') return null;
    const index = waitingPlayers.findIndex((p) => p.id === playerDoc.id);
    return index >= 0 ? index + 1 : null;
  }, [playerDoc, waitingPlayers]);

  const ensurePlayerId = () => {
    if (playerId) return playerId;
    const generated = generateId();
    setPlayerId(generated);
    if (partyId) {
      try {
        localStorage.setItem(storageKey(partyId), generated);
      } catch {}
    }
    return generated;
  };

  const handleJoin = async () => {
    if (!partyId) return;
    if (!name.trim()) {
      setError('Enter your name first.');
      return;
    }
    if (playerDoc?.status === 'playing') {
      setStatusMessage('You are already locked into the current game.');
      return;
    }
    if (playerDoc?.status === 'waiting') {
      setStatusMessage('You are already in line. Hang tight!');
      return;
    }
    try {
      setSaving(true);
      const id = ensurePlayerId();
      await upsertFarklePlayer(partyId, id, name.trim(), 'waiting');
      setStatusMessage('You are in line! Watch the big screen for your turn.');
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleLeave = async () => {
    if (!partyId || !playerId) return;
    try {
      await leaveFarkleQueue(partyId, playerId);
      setStatusMessage('Left the line. Jump back in anytime.');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleRequeue = async () => {
    if (!partyId || !playerId) return;
    try {
      await updateFarklePlayerStatus(partyId, playerId, 'waiting');
      setStatusMessage('Back in the waiting line.');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleRename = async () => {
    if (!partyId || !playerId || !name.trim()) return;
    try {
      setSaving(true);
      await renameFarklePlayer(partyId, playerId, name.trim());
      setStatusMessage('Name updated.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const phaseLabel = phase === 'active' ? 'Game in progress' : phase === 'complete' ? '🎉 Ready for next game!' : 'Waiting for next game';
  const diceTray = gameDoc?.state.rolledDice ?? [];
  const currentPlayerName = gameDoc ? gameDoc.state.players[gameDoc.state.turn] : null;
  const gameActive = !!(gameDoc && gameDoc.phase !== 'complete');
  const isPlaying = playerDoc?.status === 'playing';
  const isCurrentTurn = !!(
    isPlaying &&
    gameActive &&
    currentPlayerName === playerDoc?.name &&
    !gameDoc?.state.gameOver
  );
  const actionPending = !!playerDoc?.controlAction || pendingActionNonce !== null;
  const playerScore = playerDoc && gameDoc ? gameDoc.state.scores[playerDoc.name] ?? 0 : 0;
  const tentativeScore = isCurrentTurn && gameDoc ? gameDoc.state.tentativeScore ?? 0 : 0;
  const playerFarkleCount = playerDoc?.name && gameDoc ? gameDoc.state.stats.farkleCounts[playerDoc.name] ?? 0 : 0;
  const playerFarkleStreak = playerDoc?.name && gameDoc ? gameDoc.state.stats.farkleStreaks[playerDoc.name] ?? 0 : 0;
  const scoreboardEntries = useMemo(() => {
    if (!gameDoc) return [];
    return gameDoc.state.players
      .map((name) => ({
        name,
        score: gameDoc.state.scores[name] ?? 0,
        farkles: gameDoc.state.stats.farkleCounts[name] ?? 0,
        streak: gameDoc.state.stats.farkleStreaks[name] ?? 0,
        isCurrent: name === currentPlayerName && !gameDoc.state.gameOver,
      }))
      .sort((a, b) => b.score - a.score);
  }, [gameDoc, currentPlayerName]);
  const lastRollEvent = gameDoc?.state.lastRollEvent ?? null;
  const requiresSelectionBeforeRoll = !!(gameDoc && gameDoc.state.mustPick && gameDoc.state.rolledDice.length);


  useEffect(() => {
    if (actionPending) return;
    if (!isPlaying) {
      setControllerStatus('You are not seated in this round yet.');
      return;
    }
    if (!isCurrentTurn) {
      setControllerStatus('You are seated — wait for your turn.');
      return;
    }
    setControllerStatus('It is your turn! Use the controller below.');
  }, [isPlaying, isCurrentTurn, actionPending]);

  useEffect(() => {
    if (!isCurrentTurn) {
      setSelectedDiceIndices([]);
    }
  }, [isCurrentTurn]);

  const diceSignature = diceTray.join('-');
  useEffect(() => {
    setSelectedDiceIndices([]);
  }, [diceSignature]);

  useEffect(() => {
    if (!pendingActionNonce) return;
    if (playerDoc?.controlAckNonce === pendingActionNonce) {
      setPendingActionNonce(null);
      setControllerError(null);
      setControllerStatus('Action confirmed by the host.');
    }
  }, [playerDoc?.controlAckNonce, pendingActionNonce]);

  useEffect(() => {
    const event = lastRollEvent;
    if (!event || event.outcome !== 'farkle') {
      return;
    }
    if (lastFarkleEventRef.current === event.timestamp) {
      return;
    }
    lastFarkleEventRef.current = event.timestamp;
    if (playerDoc && event.player === playerDoc.name) {
      setFarkleDice(event.dice);
      setShowFarkleOverlay(true);
      setSelectedDiceIndices([]);
      setControllerStatus('💀 Farkle! Passing to the next player.');
      if (farkleOverlayTimerRef.current) {
        clearTimeout(farkleOverlayTimerRef.current);
      }
      farkleOverlayTimerRef.current = setTimeout(() => {
        setShowFarkleOverlay(false);
        setFarkleDice([]);
      }, 2400);
    }
  }, [lastRollEvent, playerDoc]);

  const toggleSelectedDie = (index: number) => {
    if (!isCurrentTurn || actionPending) return;
    setSelectedDiceIndices((prev) =>
      prev.includes(index) ? prev.filter((value) => value !== index) : [...prev, index].sort((a, b) => a - b)
    );
  };

  const playTurnChime = useCallback(() => {
    if (typeof window === 'undefined') return;
    const AudioCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtor) return;
    if (!turnSoundCtxRef.current) {
      turnSoundCtxRef.current = new AudioCtor();
    }
    const ctx = turnSoundCtxRef.current;
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => undefined);
    }
    const duration = 0.4;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(520, now);
    osc.frequency.exponentialRampToValueAtTime(880, now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.25, now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + duration);
  }, []);

  const prevTurnRef = useRef(isCurrentTurn);
  useEffect(() => {
    const previous = prevTurnRef.current;
    if (!previous && isCurrentTurn) {
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        navigator.vibrate(160);
      }
      playTurnChime();
    }
    prevTurnRef.current = isCurrentTurn;
  }, [isCurrentTurn, playTurnChime]);

  const sendControllerAction = useCallback(
    async (
      type: 'roll' | 'select' | 'bank',
      indices?: number[]
    ) => {
      if (!partyId || !playerDoc) return;
      const nonce = Date.now();
      setPendingActionNonce(nonce);
      setControllerError(null);
      try {
        const payload: FarklePlayerControlAction = {
          type,
          nonce,
        };
        if (indices && indices.length) {
          payload.indices = [...indices];
        }
        await sendFarklePlayerAction(partyId, playerDoc.id, payload);
        setControllerStatus('Sent to host. Watch the main screen.');
      } catch (err) {
        setPendingActionNonce(null);
        setControllerError((err as Error).message ?? 'Unable to send action.');
      }
    },
    [partyId, playerDoc]
  );

  const handleControllerRoll = useCallback(async () => {
    if (!isCurrentTurn) {
      setControllerError('Wait for your turn.');
      return;
    }
    if (actionPending) return;
    if (requiresSelectionBeforeRoll) {
      setControllerError('Select at least one scoring die before rolling again.');
      return;
    }
    await sendControllerAction('roll');
  }, [actionPending, isCurrentTurn, requiresSelectionBeforeRoll, sendControllerAction]);

  const handleControllerKeep = async () => {
    if (!isCurrentTurn) {
      setControllerError('Wait for your turn.');
      return;
    }
    if (!selectedDiceIndices.length) {
      setControllerError('Tap at least one scoring die before keeping.');
      return;
    }
    if (actionPending) return;
    await sendControllerAction('select', selectedDiceIndices);
    setControllerStatus('Submitting those dice...');
  };

  const handleControllerBank = async () => {
    if (!isCurrentTurn) {
      setControllerError('Wait for your turn.');
      return;
    }
    if (tentativeScore <= 0) {
      setControllerError('You need points before banking.');
      return;
    }
    if (actionPending) return;
    await sendControllerAction('bank');
    setSelectedDiceIndices([]);
    setControllerStatus('Bank requested...');
  };

  const renderStatusCard = () => {
    if (!playerDoc) {
      return (
        <div className="rounded-3xl border border-emerald-500/30 bg-white/10 p-6 text-center">
          <p className="text-sm text-emerald-100">Enter your name and tap join to hop into the dice line.</p>
        </div>
      );
    }
    switch (playerDoc.status) {
      case 'waiting':
        return (
          <div className="rounded-3xl border border-emerald-500/30 bg-emerald-900/30 p-6 text-center">
            <p className="text-lg font-semibold text-emerald-100">You're in line ✅</p>
            {queuePosition && (
              <p className="text-sm text-emerald-200/80">Current position: #{queuePosition}</p>
            )}
            <p className="mt-2 text-sm text-emerald-200/70">Stay nearby — the host will call out players when the next game begins.</p>
            <button
              className="mt-4 rounded-full border border-emerald-400/60 px-4 py-2 text-sm text-emerald-100 hover:bg-emerald-400/10"
              onClick={handleLeave}
            >
              Leave Line
            </button>
          </div>
        );
      case 'playing':
        return (
          <div className="rounded-3xl border border-amber-500/40 bg-amber-900/30 p-6 text-center">
            <p className="text-lg font-semibold text-amber-100">You're up in this game!</p>
            <p className="mt-2 text-sm text-amber-200/80">Use the controller below to roll, keep dice, and bank points when the host calls you.</p>
          </div>
        );
      case 'cooldown':
        return (
          <div className="rounded-3xl border border-slate-500/40 bg-slate-900/40 p-6 text-center">
            <p className="text-lg font-semibold text-slate-100">Game finished. Want back in?</p>
            <button
              className="mt-4 rounded-full border border-emerald-400/60 px-4 py-2 text-sm text-emerald-100 hover:bg-emerald-400/10"
              onClick={handleRequeue}
            >
              Rejoin the Queue
            </button>
          </div>
        );
      default:
        return (
          <div className="rounded-3xl border border-slate-500/30 bg-slate-900/30 p-6 text-center">
            <p className="text-lg font-semibold text-slate-100">You're currently inactive.</p>
            <button
              className="mt-4 rounded-full border border-emerald-400/60 px-4 py-2 text-sm text-emerald-100 hover:bg-emerald-400/10"
              onClick={handleJoin}
            >
              Jump Back In
            </button>
          </div>
        );
    }
  };

  const renderControllerPanel = () => {
    if (!isPlaying) return null;
    return (
      <div className="rounded-3xl border border-emerald-500/20 bg-gradient-to-b from-slate-950/70 to-emerald-950/40 p-6">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-emerald-300">Phone Controller</p>
            <p className="text-2xl font-semibold text-white">{isCurrentTurn ? 'It’s your turn!' : 'Sit tight'}</p>
            <p className="text-sm text-emerald-100/80">
              Banked {playerScore.toLocaleString()} pts • Tentative {tentativeScore.toLocaleString()} pts
            </p>
          </div>
          <div className="text-right text-xs text-emerald-200/70">
            <p>Current player: {currentPlayerName ?? '—'}</p>
            {playerDoc?.controlAckAt && (
              <p>Last ack: {new Date(playerDoc.controlAckAt).toLocaleTimeString()}</p>
            )}
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-4 text-sm text-emerald-100/80">
          {controllerStatus}
        </div>

        {controllerError && (
          <div className="mt-3 rounded-2xl border border-rose-400/60 bg-rose-900/30 px-4 py-3 text-sm text-rose-100">
            {controllerError}
          </div>
        )}

        <div className="mt-5">
          <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Dice Tray</p>
          {diceTray.length === 0 ? (
            <div className="mt-3 rounded-2xl border border-dashed border-emerald-400/40 px-4 py-8 text-center text-emerald-100/70">
              Waiting on a roll. The host or another player may still be finishing.
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-6">
              {diceTray.map((value: number, idx: number) => {
                const selected = selectedDiceIndices.includes(idx);
                return (
                  <button
                    key={`die-${idx}-${value}`}
                    onClick={() => toggleSelectedDie(idx)}
                    disabled={!isCurrentTurn || actionPending}
                    className={`rounded-2xl border px-3 py-2 text-center text-lg font-semibold transition ${
                      selected
                        ? 'border-lime-300/80 bg-emerald-500/30 text-white shadow-lg shadow-emerald-900'
                        : 'border-emerald-500/30 bg-slate-900/70 text-emerald-100/80'
                    } ${(!isCurrentTurn || actionPending) && 'opacity-60'}`}
                  >
                    <div className="text-2xl">🎲</div>
                    <div>{value}</div>
                    <p className="text-[0.6rem] text-emerald-200/70">{selected ? 'Selected' : 'Tap to keep'}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              if (!isCurrentTurn || actionPending) return;
              void handleControllerRoll();
            }}
            disabled={!isCurrentTurn || actionPending}
            className="flex-1 rounded-2xl bg-emerald-500 px-4 py-3 text-center text-sm font-semibold text-slate-900 hover:bg-emerald-400 disabled:bg-gray-700 disabled:text-gray-400"
          >
            🎲 Roll Dice
          </button>
          <button
            onClick={handleControllerKeep}
            disabled={!isCurrentTurn || !selectedDiceIndices.length || actionPending}
            className="flex-1 rounded-2xl border border-emerald-400 px-4 py-3 text-center text-sm font-semibold text-emerald-200 hover:bg-emerald-400/10 disabled:border-gray-700 disabled:text-gray-500"
          >
            ♻ Keep Selection
          </button>
          <button
            onClick={handleControllerBank}
            disabled={!isCurrentTurn || tentativeScore <= 0 || actionPending}
            className="flex-1 rounded-2xl border border-emerald-400/70 px-4 py-3 text-center text-sm font-semibold text-emerald-100 hover:bg-emerald-400/10 disabled:border-gray-700 disabled:text-gray-500"
          >
            💼 Bank Score
          </button>
          <button
            onClick={() => setSelectedDiceIndices([])}
            disabled={!selectedDiceIndices.length || actionPending}
            className="rounded-2xl border border-emerald-400/40 px-4 py-3 text-sm text-emerald-100/80 hover:bg-emerald-400/5 disabled:border-gray-700 disabled:text-gray-500"
          >
            Clear Selection
          </button>
        </div>

        {actionPending && (
          <p className="mt-3 text-xs text-amber-200/90">Sent to host — we’ll update once it lands.</p>
        )}
      </div>
    );
  };

  if (!partyId) {
    return <div className="p-6">Missing party info.</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-950 via-slate-950 to-slate-900 text-white">
      {showFarkleOverlay && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.5em] text-rose-200">{playerDoc?.name ?? 'You'} farkled</p>
            <p className="mt-2 text-5xl font-black uppercase tracking-[0.6em] text-rose-200 drop-shadow-[0_0_35px_rgba(248,113,113,0.75)] animate-pulse">
              FARKLE
            </p>
            <p className="mt-4 text-base font-semibold text-rose-100">No scoring dice — next player steps up.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {farkleDice.map((value, idx) => (
                <div key={`farkle-die-${idx}`} className="flex h-14 w-14 items-center justify-center rounded-2xl border border-rose-400/60 bg-rose-900/40 text-2xl font-bold text-rose-100">
                  {value}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-5 py-10">
        <header className="rounded-3xl border border-emerald-500/30 bg-emerald-950/60 p-6 shadow-2xl shadow-emerald-900/40">
          <p className="text-xs uppercase tracking-[0.4em] text-emerald-300">Frog Lab</p>
          <h1 className="text-3xl font-semibold">Join the Farkle Line</h1>
          <p className="text-sm text-emerald-100/80">Party {partyId}</p>
          <p className="mt-3 text-xs uppercase tracking-[0.3em] text-emerald-300">{phaseLabel}</p>
        </header>

        <div className="rounded-3xl border border-emerald-500/20 bg-slate-950/70 p-6 space-y-4">
          <label className="text-sm font-semibold text-emerald-100" htmlFor="player-name">
            Your Name
          </label>
          <input
            id="player-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Frog Fan"
            className="w-full rounded-2xl border border-emerald-500/40 bg-transparent px-4 py-3 text-lg focus:border-emerald-300 focus:outline-none"
          />
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleJoin}
              disabled={saving || playerDoc?.status === 'waiting' || playerDoc?.status === 'playing'}
              className="rounded-2xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-emerald-400 disabled:bg-gray-700 disabled:text-gray-400"
            >
              {playerDoc ? 'Rejoin Queue' : 'Join Queue'}
            </button>
            {playerDoc && (
              <button
                onClick={handleRename}
                disabled={saving || !name.trim()}
                className="rounded-2xl border border-emerald-400 px-4 py-2 text-sm font-semibold text-emerald-200 hover:bg-emerald-400/10 disabled:border-gray-700 disabled:text-gray-500"
              >
                Save Name
              </button>
            )}
          </div>
          {error && <p className="text-sm text-rose-300">{error}</p>}
          <p className="text-sm text-emerald-100/80">{statusMessage}</p>
        </div>

        {renderStatusCard()}

        {renderControllerPanel()}

        {scoreboardEntries.length > 0 && (
          <div className="rounded-3xl border border-emerald-500/20 bg-slate-950/70 p-6">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Leaderboard</p>
              <span className="text-[0.65rem] uppercase tracking-[0.2em] text-emerald-200/70">First to 10K</span>
            </div>
            <div className="mt-4 space-y-2">
              {scoreboardEntries.slice(0, 5).map((entry, idx) => (
                <div
                  key={entry.name}
                  className={`flex items-center justify-between rounded-2xl border px-4 py-3 ${
                    entry.isCurrent
                      ? 'border-emerald-400/60 bg-emerald-900/40 text-emerald-100'
                      : 'border-transparent bg-emerald-900/20 text-emerald-100/80'
                  }`}
                >
                  <div>
                    <p className="text-sm font-semibold">#{idx + 1} {entry.name}</p>
                    <p className="text-xs text-emerald-200/70">
                      {entry.isCurrent ? 'Taking a turn' : 'Waiting'} • 💀 {entry.farkles} • Streak {entry.streak}
                    </p>
                  </div>
                  <p className="text-lg font-bold text-emerald-100">{entry.score.toLocaleString()}</p>
                </div>
              ))}
            </div>
            {scoreboardEntries.length > 5 && (
              <p className="mt-2 text-xs text-emerald-200/70">+{scoreboardEntries.length - 5} more players in this round</p>
            )}
          </div>
        )}

        <div className="rounded-3xl border border-emerald-500/20 bg-slate-950/70 p-6">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Waiting Line</p>
            <span className="text-[0.65rem] uppercase tracking-[0.2em] text-emerald-200/70">{waitingPlayers.length}</span>
          </div>
          {waitingPlayers.length === 0 ? (
            <p className="mt-4 text-sm text-emerald-100/70">You're early! Stick around and more singers will pile in.</p>
          ) : (
            <div className="mt-4 space-y-2">
              {waitingPlayers.slice(0, 6).map((player, idx) => (
                <div key={player.id} className="flex items-center justify-between rounded-xl border border-emerald-500/20 bg-emerald-900/30 px-3 py-2">
                  <div>
                    <p className="text-sm font-semibold text-emerald-100">{player.name}</p>
                    <p className="text-xs text-emerald-200/70">#{idx + 1}</p>
                  </div>
                  {playerDoc && playerDoc.id === player.id && (
                    <span className="rounded-full bg-emerald-500/20 px-3 py-1 text-xs text-emerald-200">You</span>
                  )}
                </div>
              ))}
              {waitingPlayers.length > 6 && (
                <p className="text-xs text-emerald-200/70">+{waitingPlayers.length - 6} more</p>
              )}
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-emerald-500/20 bg-slate-950/70 p-6 text-sm text-emerald-100/80">
          <p className="font-semibold text-emerald-200">How it works</p>
          <ul className="mt-3 space-y-2 text-emerald-100/70 list-disc list-inside">
            <li>Scan the QR on the main screen any time — you'll join the waiting line automatically.</li>
            <li>When a game starts, the top players in line are locked in. Everyone else sticks around for the next round.</li>
            <li>If you leave or finish a game, rejoin with one tap using this page.</li>
          </ul>
        </div>

        <div className="text-center text-sm text-emerald-100/60">
          <Link to={`/party/${partyId}/game/farkle/live`} className="text-emerald-300 underline">

            <div className="mt-4 rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-4">
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-emerald-300">
                <span>Farkle Counter</span>
                <span>3 strikes = -1k</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-center">
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-900/40 px-3 py-2">
                  <p className="text-[0.65rem] uppercase tracking-[0.2em] text-emerald-200/70">Streak</p>
                  <p className={`text-2xl font-bold ${playerFarkleStreak >= 2 ? 'text-rose-300' : 'text-emerald-100'}`}>{playerFarkleStreak}</p>
                </div>
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-900/40 px-3 py-2">
                  <p className="text-[0.65rem] uppercase tracking-[0.2em] text-emerald-200/70">Total</p>
                  <p className="text-2xl font-bold text-emerald-100">{playerFarkleCount}</p>
                </div>
              </div>
              <p className="mt-2 text-[0.7rem] text-emerald-200/70">
                {playerFarkleStreak >= 2 ? 'Careful — one more Farkle costs 1,000 points.' : 'Stay hot. Three Farkles in a row triggers the penalty.'}
              </p>
            </div>
            Host view
          </Link>{' '}
          ·
          <Link to={`/party/${partyId}`} className="text-emerald-300 underline">
            Back to show screen
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FarkleJoinPage;
